/**
 * VoiceMemoPlayer Component
 * 
 * iMessage-style audio player for voice memos.
 * Features:
 * - Play/pause toggle
 * - Waveform-style progress bar (clickable to seek)
 * - Playback speed controls: 1x, 1.5x, 2x
 * - Time display (current/total)
 * - Auto-reset when finished
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';

export default function VoiceMemoPlayer({ 
  voiceMemo, 
  isOwnMessage = false,
  compact = false 
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(voiceMemo?.duration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Update playback rate when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      // Use audio duration if valid, otherwise fall back to voiceMemo.duration
      if (isFinite(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      }
      setIsLoaded(true);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  const handleProgressClick = useCallback((e) => {
    if (!progressRef.current || !audioRef.current || !isLoaded) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration, isLoaded]);

  const cyclePlaybackRate = useCallback(() => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  }, [playbackRate]);

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get the audio source URL
  const audioSrc = voiceMemo?.url || voiceMemo?.dataUrl || '';

  // Color scheme based on message ownership
  const colors = isOwnMessage ? {
    bg: 'bg-blue-400/30',
    progressBg: 'bg-blue-300/50',
    progressFill: 'bg-white/80',
    playBtn: 'bg-white text-blue-500',
    playBtnHover: 'hover:bg-blue-50 dark:hover:bg-blue-500/15',
    text: 'text-blue-100',
    speedBtn: 'bg-blue-400/50 text-white hover:bg-blue-400/70',
    waveActive: 'bg-white/90',
    waveInactive: 'bg-blue-200/50'
  } : {
    bg: 'bg-slate-100 dark:bg-[#3a3d41]',
    progressBg: 'bg-slate-200 dark:bg-[#3e4145]',
    progressFill: 'bg-blue-500',
    playBtn: 'bg-blue-500 text-white',
    playBtnHover: 'hover:bg-blue-600',
    text: 'text-slate-500 dark:text-slate-400',
    speedBtn: 'bg-slate-200 dark:bg-[#3e4145] text-slate-600 dark:text-slate-300 hover:bg-slate-300',
    waveActive: 'bg-blue-500',
    waveInactive: 'bg-slate-300'
  };

  return (
    <div 
      data-ui="voice"
      data-mine={isOwnMessage ? 'true' : undefined}
      className={`rounded-xl p-3 ${colors.bg} min-w-[200px] max-w-[280px]`}
      data-testid="voice-memo-player"
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={(e) => {
          console.error('Audio playback error:', e.target?.error?.message || 'Unknown error');
          // For URL-based audio (DMs), the browser reads Content-Type from the server.
          // If the format isn't supported (e.g., WebM on old iOS), there's no client-side fallback.
        }}
        preload="metadata"
      />
      
      <div className="flex items-center gap-3">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          data-ui="voice-play"
          className={`w-10 h-10 ${colors.playBtn} ${colors.playBtnHover} rounded-full flex items-center justify-center shadow-sm transition-all hover:scale-105 flex-shrink-0`}
          data-testid="voice-player-play-btn"
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>

        {/* Progress & Controls */}
        <div className="flex-1 min-w-0">
          {/* Waveform-style progress bar */}
          <div 
            ref={progressRef}
            data-ui="voice-track"
            className={`h-8 ${colors.progressBg} rounded-lg cursor-pointer relative overflow-hidden`}
            onClick={handleProgressClick}
            data-testid="voice-player-progress"
          >
            {/* Progress fill */}
            <div 
              data-ui="voice-fill"
              className={`absolute inset-y-0 left-0 ${colors.progressFill} transition-all duration-100 rounded-lg`}
              style={{ width: `${progress}%` }}
            />
            
            {/* Waveform visualization (static bars) */}
            <div className="absolute inset-0 flex items-center justify-around px-1">
              {[...Array(20)].map((_, i) => {
                // Generate pseudo-random but consistent heights
                const seed = (i * 7 + 3) % 17;
                const height = 25 + (seed / 17) * 50 + Math.sin(i * 0.8) * 15;
                const isActive = (i / 20) * 100 <= progress;
                return (
                  <div
                    key={i}
                    className={`w-0.5 rounded-full transition-colors duration-150 ${
                      isActive ? colors.waveActive : colors.waveInactive
                    }`}
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Time & Speed Controls */}
          <div className="flex items-center justify-between mt-1.5">
            <span data-ui="voice-time" className={`text-[10px] ${colors.text} font-medium`}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              onClick={cyclePlaybackRate}
              data-ui="voice-speed"
              className={`text-[10px] font-semibold ${colors.speedBtn} px-1.5 py-0.5 rounded transition-colors`}
              data-testid="voice-player-speed-btn"
              title="Change playback speed"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
